/**
 * Currency utility functions for dynamic currency handling
 */

import { useState, useEffect } from 'react';
import { activeLocale, numberLocale } from './locale';

/**
 * Get currency symbol from restaurant data
 * @param {string} currency - Currency code from restaurant data
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = "USD") => {
  const cur = String(currency || "").trim().toLowerCase();
  // the dinar however a restaurant sheet or an API spells it (« TND », « tnd  », « Tunisian Dinar »,
  // « dinar tunisien », « د.ت », « DT ») reads « DT » — never « TND » on a screen (owner 14/10)
  if (/^(tnd|dt)$|tunisi|^د\.?\s?ت$/.test(cur)) return "DT";
  // Europe
  if (cur === "euro" || cur === "eur") return "€";
  if (cur === "gbp" || cur === "pound" || cur === "sterling") return "£";
  if (cur === "chf") return "Fr";
  if (cur === "sek") return "kr"; // Sweden
  if (cur === "nok") return "kr"; // Norway
  if (cur === "dkk") return "kr"; // Denmark
  if (cur === "pln") return "zł"; // Poland
  // Americas
  if (cur === "usd" || cur === "dollar") return "$";
  if (cur === "cad") return "CA$";
  if (cur === "aud") return "A$"; // Australia
  if (cur === "brl") return "R$"; // Brazil
  if (cur === "mxn") return "MX$"; // Mexico
  if (cur === "ars") return "$"; // Argentina
  if (cur === "cop") return "$"; // Colombia
  // Asia & Pacific
  if (cur === "jpy" || cur === "yen") return "¥";
  if (cur === "cny" || cur === "rmb" || cur === "yuan") return "¥";
  if (cur === "krw") return "₩"; // South Korea
  if (cur === "inr" || cur === "rupee") return "₹"; // India
  if (cur === "sgd") return "S$"; // Singapore
  if (cur === "hkd") return "HK$"; // Hong Kong
  if (cur === "thb") return "฿"; // Thailand
  if (cur === "idr") return "Rp"; // Indonesia
  if (cur === "myr") return "RM"; // Malaysia
  // Middle East & Africa
  if (cur === "tnd" || cur === "dinar") return "DT"; // how Tunisians write the dinar (owner rule, QA 13/10 B15)
  if (cur === "mad") return "MAD"; // Morocco
  if (cur === "dzd") return "DZD"; // Algeria
  if (cur === "egp") return "EGP"; // Egypt
  if (cur === "sar") return "﷼"; // Saudi Arabia
  if (cur === "aed") return "د.إ"; // UAE
  if (cur === "qar") return "﷼"; // Qatar
  if (cur === "kwd") return "KD"; // Kuwait
  if (cur === "bhd") return "BD"; // Bahrain
  if (cur === "omr") return "﷼"; // Oman
  if (cur === "lyd") return "LYD"; // Libya
  if (cur === "xof") return "CFA"; // West Africa
  if (cur === "xaf") return "CFA"; // Central Africa
  if (cur === "zar") return "R"; // South Africa
  if (cur === "ngn") return "₦"; // Nigeria
  if (cur === "ghs") return "₵"; // Ghana
  if (cur === "mru") return "MRU"; // Mauritania
  return String(currency || "").trim().toUpperCase(); // Fallback to currency code
};

/**
 * Fetch restaurant data for employees who don't have it in localStorage
 * @returns {Object|null} Restaurant data
 */
const fetchRestaurantDataForEmployee = async () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const userObj = JSON.parse(localStorage.getItem('userObj') || '{}');
    if (!userObj.access_token || !userObj.restaurant_id) {
      return null;
    }

    // Dynamically import to avoid circular dependency
    const { getSingleRestaurant } = await import('@/api');
    const response = await getSingleRestaurant();
    
    if (response?.data) {
      // Store restaurant data for future use
      localStorage.setItem("restaurant_data", JSON.stringify(response.data));
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching restaurant data for employee:", error);
  }
  
  return null;
};

/**
 * Get restaurant currency from localStorage or fetch it for employees
 * @returns {string} Currency code
 */
export const getRestaurantCurrency = async () => {
  if (typeof window === 'undefined') return "USD";
  
  // First try to get from restaurant_data (works for owners)
  const restaurantDataString = localStorage.getItem("restaurant_data");
  if (restaurantDataString) {
    try {
      const restaurantData = JSON.parse(restaurantDataString);
      return restaurantData.currency || "USD";
    } catch (error) {
      console.error("Error parsing restaurant_data:", error);
    }
  }
  
  // If no restaurant_data, check if user is an employee and fetch restaurant data
  const userObj = JSON.parse(localStorage.getItem('userObj') || '{}');
  if (userObj.role_name === 'employee' && userObj.restaurant_id) {
    const restaurantData = await fetchRestaurantDataForEmployee();
    if (restaurantData?.currency) {
      return restaurantData.currency;
    }
  }
  
  return "USD";
};

/**
 * Synchronous version for contexts where async is not suitable
 * @returns {string} Currency code
 */
export const getRestaurantCurrencySync = () => {
  if (typeof window === 'undefined') return "USD";
  
  const restaurantDataString = localStorage.getItem("restaurant_data");
  if (restaurantDataString) {
    try {
      const restaurantData = JSON.parse(restaurantDataString);
      return restaurantData.currency || "USD";
    } catch (error) {
      console.error("Error parsing restaurant_data:", error);
    }
  }
  return "USD";
};

/**
 * Hook to get dynamic currency symbol that updates on currency changes
 * @returns {string} Currency symbol
 */
export const useCurrencySymbol = () => {
  // Start from the synchronously-known currency (restaurant_data in localStorage)
  // so owners never see a wrong-symbol flash; employees update after the fetch.
  const [currencySymbol, setCurrencySymbol] = useState(() => getCurrencySymbol(getRestaurantCurrencySync()));
  
  useEffect(() => {
    const handleCurrencyChange = async () => {
      try {
        const newCurrency = await getRestaurantCurrency();
        const newSymbol = getCurrencySymbol(newCurrency);
        setCurrencySymbol(newSymbol);
      } catch (error) {
        console.error("Error fetching currency:", error);
        // Try sync version as fallback
        const syncCurrency = getRestaurantCurrencySync();
        setCurrencySymbol(getCurrencySymbol(syncCurrency));
      }
    };

    // Initial load - always try to get the correct currency
    handleCurrencyChange();

    // Listen for custom currency change events
    const handleStorageChange = () => {
      handleCurrencyChange();
    };

    window.addEventListener('currencyChanged', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('currencyChanged', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty dependency array - run only once

  return currencySymbol;
};

/**
 * Format price with currency symbol
 * @param {number} amount - Price amount
 * @param {string} currency - Currency code (optional, will get from localStorage if not provided)
 * @returns {string} Formatted price with currency
 */
export const formatPrice = (amount, currency = null) => {
  const currencyCode = currency || getRestaurantCurrencySync();
  const symbol = getCurrencySymbol(currencyCode);
  // grouped and punctuated for the reader (« 89 117,88 TND », not « 89117.88 TND »)
  const formattedAmount = new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(amount || 0));
  
  // The symbol after the amount, as everywhere else (formatMoney): « 12,50 DT », « 12,50 € »,
  // never « TND12,50 » (QA 13/10 B8).
  return `${formattedAmount} ${symbol}`;
};

// ---------------------------------------------------------------------------
// Writing an amount the way the reader's country writes it
// ---------------------------------------------------------------------------
// A French restaurant reads "1 234,56 €", not "1234.56 €". Half the product
// already formatted amounts through the browser's locale and half pasted
// toFixed(2) next to a symbol, so the same figure changed shape from one page
// to the next. These two helpers are the single place that decides.

// La résolution de langue vit dans utils/locale.js — les dates en avaient besoin
// autant que les montants. Réexportée ici pour ne casser aucun import existant.
export { activeLocale, numberLocale } from './locale';

/**
 * An amount, grouped and punctuated for the reader, followed by the symbol.
 * `currency` is the SYMBOL (what useCurrencySymbol returns), not the ISO code,
 * because that is what every call site already holds.
 */
export const formatMoney = (value, currency = '€', { decimals = 2, locale } = {}) => {
  // an ISO code handed by an API (« TND », « EUR ») is written the reader's way (« DT », « € ») — QA 13/10 B15
  if (typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency.trim())) currency = getCurrencySymbol(currency.trim());
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const text = new Intl.NumberFormat(numberLocale(locale), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safe);
  return currency ? `${text} ${currency}` : text;
};

/** The same grouping, without a currency — quantities, counts, points. */
export const formatNumber = (value, { decimals = 0, locale } = {}) =>
  formatMoney(value, '', { decimals, locale });

/** Decimals for a Co-Gérant « money at stake »: whole above 100, cents below — the card and the decision
 *  queue round alike (QA 13/10 4th pass R5: « 38 DT » there, « 37,62 DT » here). */
export const moneyDecimals = (v) => (Math.abs(Number(v)) >= 100 ? 0 : 2);
